import { Component, computed, inject } from '@angular/core';
import { _, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { mgcIconDocument2Regular } from '@sb/mgc-icons/mgcIcon-document_2_regular.icon';
import { ButtonComponent } from '@sb/ui/button';
import { DIALOG_DATA, DialogRef, SbDialogModule } from '@sb/ui/dialog';
import { EmptyStateComponent } from '@sb/ui/feedback';
import { IconRegistry } from '@sb/ui/icon';
import { StepperComponent, type Step } from '@sb/ui/stepper';

import { TemplateCategory } from '../../models/contract-template.model';
import { ContractTemplateUploadDialog } from '../../services/contract-template-upload-dialog';
import { CheckFieldsStepComponent } from '../check-fields-step/check-fields-step.component';
import type { EntryDecision } from '../check-fields-step/field-card/field-card.component';
import { DetectionProgressComponent } from '../detection-progress/detection-progress.component';
import { AiTemplateBuilderStore, type ReviewHandoff } from '../services/ai-template-builder.store';
import { WhoSignsStepComponent } from '../step-who-signs/who-signs-step.component';
import { UploadStepComponent } from '../upload-step/upload-step.component';

const REVIEW_STEPS = [_('Signing'), _('Check fields'), _('Save')];

export interface ReviewDialogData extends ReviewHandoff {
  category?: TemplateCategory;
}

/**
 * The fullscreen review steps of the AI template builder, under the shared stepper next to the document.
 * A fixed document uploaded from the signing step is scanned here again, with the same views as the upload dialog.
 */
@Component({
  selector: 'ai-template-review-modal',
  templateUrl: './ai-template-review-modal.component.html',
  imports: [
    ButtonComponent,
    CheckFieldsStepComponent,
    DetectionProgressComponent,
    EmptyStateComponent,
    SbDialogModule,
    StepperComponent,
    TranslatePipe,
    UploadStepComponent,
    WhoSignsStepComponent,
  ],
  providers: [AiTemplateBuilderStore],
})
export class AiTemplateReviewModalComponent {
  #translate = inject(TranslateService);
  #dialogRef = inject(DialogRef);
  #uploadDialog = inject(ContractTemplateUploadDialog);
  #data = inject<ReviewDialogData>(DIALOG_DATA);

  store = inject(AiTemplateBuilderStore);

  isExtension = this.#data.category === TemplateCategory.EXTENSION;

  steps = computed<Step[]>(() => REVIEW_STEPS.map((label) => ({ label: this.#translate.instant(label) })));

  progressState = computed(() => {
    const state = this.store.detectionState();

    return state === null || state === 'resolved' ? null : state;
  });

  constructor() {
    inject(IconRegistry).registerIcons([mgcIconDocument2Regular]);

    const { fileName, resourceId, data } = this.#data;

    this.store.startReview({ fileName, resourceId, data });
  }

  setUpManually(): void {
    this.#dialogRef.close();
    this.#uploadDialog.openManualUpload({ category: this.#data.category });
  }

  applyDecisions(decisions: EntryDecision[]): void {
    for (const { entryKey, decision } of decisions) {
      this.store.setBlankDecision(entryKey, decision);
    }
  }
}
